import { useEffect } from "react";

type SeoProps = {
  title?: string;
  description?: string;
  canonicalPath?: string; // e.g., "/look/123"
  images?: string[]; // absolute or relative; first used for OG/Twitter
  robots?: string; // e.g., "index,follow" or "noindex,follow"
  type?: "website" | "article" | "product" | "profile" | "video.other";
  /** Route-level schema.org object. Replaces the snapshot's head block
   *  rather than adding a second competing entity for the same URL. */
  jsonLd?: Record<string, unknown>;
};

const DEFAULT_SOCIAL_IMAGE = "/garu-social-share.png";

function ensureAbsoluteUrl(pathOrUrl: string, origin: string): string {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  if (pathOrUrl.startsWith("/")) return `${origin}${pathOrUrl}`;
  return `${origin}/${pathOrUrl}`;
}

function setOrCreateMeta(selector: { name?: string; property?: string }, content: string | undefined) {
  const attrKey = selector.name ? "name" : "property";
  const attrVal = selector.name || selector.property || "";
  if (!attrVal) return;
  let el = document.head.querySelector(`meta[${attrKey}='${attrVal}']`) as HTMLMetaElement | null;
  if (!content) {
    if (el) el.parentElement?.removeChild(el);
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrKey, attrVal);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setOrCreateLink(rel: string, href: string | undefined) {
  let el = document.head.querySelector(`link[rel='${rel}']`) as HTMLLinkElement | null;
  if (!href) {
    if (el) el.parentElement?.removeChild(el);
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function Seo({ title, description, canonicalPath, images, robots, type = "website", jsonLd }: SeoProps) {
  useEffect(() => {
    const siteUrl = (import.meta as any).env?.VITE_BASE_URL || window.location.origin;

    // Title
    if (title) document.title = title;

    // Description
    setOrCreateMeta({ name: "description" }, description);

    // Robots
    setOrCreateMeta({ name: "robots" }, robots);
    setOrCreateMeta({ name: "googlebot" }, robots);

    // Canonical
    const canonicalAbs = ensureAbsoluteUrl(canonicalPath || window.location.pathname, siteUrl);
    setOrCreateLink("canonical", canonicalAbs);

    // Open Graph
    setOrCreateMeta({ property: "og:title" }, title);
    setOrCreateMeta({ property: "og:description" }, description);
    setOrCreateMeta({ property: "og:type" }, type);
    setOrCreateMeta({ property: "og:url" }, canonicalAbs);
    const ogImage = ensureAbsoluteUrl(
      images && images.length > 0 ? images[0] : DEFAULT_SOCIAL_IMAGE,
      siteUrl
    );
    setOrCreateMeta({ property: "og:image" }, ogImage);
    setOrCreateMeta({ property: "og:image:width" }, "1200");
    setOrCreateMeta({ property: "og:image:height" }, "630");
    setOrCreateMeta({ property: "og:site_name" }, "garu");

    // Twitter
    setOrCreateMeta({ name: "twitter:card" }, ogImage ? "summary_large_image" : "summary");
    setOrCreateMeta({ name: "twitter:title" }, title);
    setOrCreateMeta({ name: "twitter:description" }, description);
    setOrCreateMeta({ name: "twitter:image" }, ogImage);
  }, [title, description, canonicalPath, robots, type, images?.[0]]);

  // Route-level structured data.
  //
  // The build-time snapshot writes a JSON-LD block into <head> tagged
  // with data-seo-jsonld. React only replaces #root on mount, so a page
  // that ALSO renders its own <script type="application/ld+json"> ends
  // up publishing two competing entities for one URL — /faq was serving
  // a 3-question FAQPage in the head and a 14-question one in the body,
  // which violates Google's rule that FAQ markup match visible content.
  // Routing it through here instead replaces the snapshot block in
  // place, so there is exactly one entity per URL, and client-side
  // navigation updates it rather than stacking another one on.
  useEffect(() => {
    const MARKER = "data-seo-jsonld";
    let el = document.head.querySelector(`script[${MARKER}]`) as HTMLScriptElement | null;
    if (!jsonLd) {
      el?.parentElement?.removeChild(el);
      return;
    }
    if (!el) {
      el = document.createElement("script");
      el.setAttribute("type", "application/ld+json");
      el.setAttribute(MARKER, "");
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(jsonLd);
  }, [jsonLd]);

  return null;
}

export default Seo;










